/**
 * src/pages/admin/AdminPopupManager.js
 *
 * Admin page for sending targeted and broadcast popups to users.
 * Integrates into AdminLayout at /admin/popups.
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Send,
  Users,
  User,
  Bell,
  Loader,
  CheckCircle,
  Trash2,
  Eye,
  EyeOff,
  Clock,
  RefreshCw,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAdmin } from "../../hooks/useAdmin";
import * as feedbackService from "../../services/feedbackService";
import { getDocs, collection, query, limit, orderBy } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { errorDev } from '../../utils/logger.js';

function formatTime(ts) {
  if (!ts) return "—";
  const d = ts?.toDate ? ts.toDate() : new Date(ts?.seconds ? ts.seconds * 1000 : ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export default function AdminPopupManager() {
  const { isSuperAdmin, user: adminUser } = useAdmin();

  // Send form state
  const [mode, setMode] = useState("broadcast"); // broadcast | targeted
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [actionLabel, setActionLabel] = useState("");
  const [actionUrl, setActionUrl] = useState("");
  const [targetEmails, setTargetEmails] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // Popup history
  const [popups, setPopups] = useState([]);
  const [loadingPopups, setLoadingPopups] = useState(true);
  const [expandedPopup, setExpandedPopup] = useState(null);

  // Load popup history
  const loadPopups = useCallback(() => {
    setLoadingPopups(true);
    const unsub = feedbackService.listenAllPopups((items) => {
      setPopups(items);
      setLoadingPopups(false);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const unsub = loadPopups();
    return () => unsub();
  }, [loadPopups]);

  // Resolve emails to user IDs
  const resolveEmails = async (emailStr) => {
    const emails = emailStr
      .split(/[,;\n]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

    const userIds = [];
    for (const email of emails) {
      const snap = await getDocs(
        query(collection(db, "users"), limit(500))
      );
      const match = snap.docs.find(
        (d) => d.data().email?.toLowerCase() === email
      );
      if (match) userIds.push(match.id);
    }
    return userIds;
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!title.trim() || !message.trim()) return;

    setSending(true);
    setSendResult(null);

    try {
      let results;
      if (mode === "broadcast") {
        results = await feedbackService.sendBroadcastPopup(adminUser, {
          title: title.trim(),
          message: message.trim(),
          actionLabel: actionLabel.trim() || "",
          actionUrl: actionUrl.trim() || "",
        });
      } else {
        const userIds = await resolveEmails(targetEmails);
        if (userIds.length === 0) {
          setSendResult({ success: false, message: "No matching users found" });
          setSending(false);
          return;
        }
        results = await feedbackService.sendTargetedPopup(adminUser, {
          userIds,
          title: title.trim(),
          message: message.trim(),
          actionLabel: actionLabel.trim() || "",
          actionUrl: actionUrl.trim() || "",
        });
      }

      const successCount = results.filter((r) => r.success).length;
      setSendResult({
        success: true,
        message: `Popup sent to ${successCount} user${successCount !== 1 ? "s" : ""}`,
      });
      setTitle("");
      setMessage("");
      setActionLabel("");
      setActionUrl("");
      setTargetEmails("");
    } catch (err) {
      setSendResult({ success: false, message: err.message || "Failed to send" });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (popupId) => {
    try {
      await feedbackService.deletePopup(popupId);
    } catch (err) {
      // Silent
    errorDev("Operation failed:", err);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div>
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                Popup Manager
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                Send targeted or broadcast popups to users
              </p>
            </div>
            <Bell className="w-5 h-5 text-gray-400" />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24 space-y-6">
        {/* ─── Send New Popup ─── */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5">
          <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Send className="w-4 h-4 text-green-600 dark:text-green-400" />
            Send New Popup
          </h2>

          {/* Mode Toggle */}
          <div className="flex gap-2 mb-4">
            <button
              type="button"
              onClick={() => setMode("broadcast")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                mode === "broadcast"
                  ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 ring-1 ring-green-500/30"
                  : "bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400"
              }`}
            >
              <Users className="w-4 h-4" />
              Broadcast
            </button>
            <button
              type="button"
              onClick={() => setMode("targeted")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                mode === "targeted"
                  ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 ring-1 ring-blue-500/30"
                  : "bg-gray-100 dark:bg-neutral-700 text-gray-600 dark:text-gray-400"
              }`}
            >
              <User className="w-4 h-4" />
              Targeted
            </button>
          </div>

          <form onSubmit={handleSend} className="space-y-3">
            {/* Targeted Emails */}
            {mode === "targeted" && (
              <div>
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 block">
                  Target Emails (comma separated)
                </label>
                <textarea
                  value={targetEmails}
                  onChange={(e) => setTargetEmails(e.target.value)}
                  placeholder="user1@gmail.com, user2@gmail.com"
                  rows={2}
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition"
                />
              </div>
            )}

            {/* Title */}
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Popup title..."
              maxLength={120}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
            />

            {/* Message */}
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Popup message..."
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none transition"
            />

            {/* Optional Action Button */}
            <div className="grid grid-cols-2 gap-3">
              <input
                value={actionLabel}
                onChange={(e) => setActionLabel(e.target.value)}
                placeholder="Button label (optional)"
                maxLength={40}
                className="px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
              <input
                value={actionUrl}
                onChange={(e) => setActionUrl(e.target.value)}
                placeholder="Action URL (optional)"
                maxLength={200}
                className="px-3 py-2 rounded-xl border border-gray-200 dark:border-neutral-700 bg-gray-50 dark:bg-neutral-900 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-green-500 focus:border-transparent transition"
              />
            </div>

            {/* Result */}
            {sendResult && (
              <div
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                  sendResult.success
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                    : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400"
                }`}
              >
                {sendResult.success ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                {sendResult.message}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={sending || !title.trim() || !message.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 rounded-xl transition disabled:opacity-50"
            >
              {sending ? (
                <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {mode === "broadcast" ? "Send to All Users" : "Send to Selected Users"}
            </button>
          </form>
        </div>

        {/* ─── Popup History ─── */}
        <div className="bg-white dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-400" />
              Recent Popups
            </h2>
            <span className="text-xs text-gray-400">{popups.length} total</span>
          </div>

          {loadingPopups ? (
            <div className="flex justify-center py-8">
              <span className="orrith-loader orrith-loader-sm orrith-loader-primary" />
            </div>
          ) : popups.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">No popups sent yet</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {popups.map((p) => (
                <div
                  key={p.id}
                  className="bg-gray-50 dark:bg-neutral-900 rounded-xl border border-gray-100 dark:border-neutral-700 p-3"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span
                          className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            p.seen ? "bg-gray-300 dark:bg-neutral-600" : "bg-green-500"
                          }`}
                        />
                        <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {p.title}
                        </h4>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">
                        {p.message}
                      </p>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          {p.seen ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                          {p.seen ? "Seen" : "Unseen"}
                        </span>
                        <span>{formatTime(p.createdAt)}</span>
                        <span className="capitalize">{p.source || p.type}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => setExpandedPopup(expandedPopup === p.id ? null : p.id)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-neutral-700 rounded transition"
                      >
                        {expandedPopup === p.id ? (
                          <ChevronUp className="w-4 h-4 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400" />
                        )}
                      </button>
                      {isSuperAdmin && (
                        <button
                          onClick={() => handleDelete(p.id)}
                          className="p-1 hover:bg-red-100 dark:hover:bg-red-900/20 rounded text-gray-400 hover:text-red-500 transition"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedPopup === p.id && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-neutral-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                      <p><strong>User:</strong> {p.userId}</p>
                      <p><strong>Type:</strong> {p.type}</p>
                      {p.sentByEmail && <p><strong>Sent by:</strong> {p.sentByEmail}</p>}
                      {p.actionLabel && <p><strong>Action:</strong> {p.actionLabel} → {p.actionUrl}</p>}
                      <p><strong>Full message:</strong> {p.message}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
